import { MuteHistoryEntry } from "api/dropsLocalStorage";
import { MeasureCardHeaderLabel } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/MeasureCard/MeasureCardHeaderLabel";
import { MeasureOverviewDataContext } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewDataContext";
import { FC, useContext, useState } from "react";
import {
  Button,
  DialogCloseTrigger,
  DialogRoot,
  DialogTrigger,
  DropsDialogContent,
  Flex,
  StaticCard,
  Text,
  Textarea,
} from "shared/components/Spor";
import { useThemeSemantic } from "shared/theme/themeSemantic";
import { CombinedHistoryResponse } from "shared/types/operationalInformation";
import { Semantics } from "shared/utils/semantics";

type MuteDialogProps = {
  information: CombinedHistoryResponse;
  isMuted: boolean;
  addMuteHistoryEntry: (entry: MuteHistoryEntry) => void;
  semantics: Semantics;
};

export const MuteDialog: FC<MuteDialogProps> = ({
  information,
  isMuted,
  addMuteHistoryEntry,
  semantics,
}) => {
  const theme = useThemeSemantic(semantics);

  const { forceRefresh: forceGlobalRecount } = useContext(
    MeasureOverviewDataContext,
  );

  const [muteDialogOpen, setMuteDialogOpen] = useState(false);
  const [reason, setReason] = useState("");

  const actionVerb = isMuted ? "Fremhev" : "Demp";

  return (
    <DialogRoot
      open={muteDialogOpen}
      onInteractOutside={() => setMuteDialogOpen(false)}
      placement="center"
      size="sm"
    >
      <DialogTrigger asChild>
        <Button
          variant="tertiary"
          size="xs"
          outlineColor={theme.colorSemanticOutline}
          onClick={(e) => {
            e.stopPropagation();
            setMuteDialogOpen(true);
          }}
        >
          {actionVerb}
        </Button>
      </DialogTrigger>

      <DropsDialogContent>
        <Flex direction="column" gap={1} p={4}>
          <Text variant="md-lg" fontWeight="bold">
            {actionVerb} hendelse
          </Text>
          <MeasureCardHeaderLabel information={information} />
        </Flex>
        <DialogCloseTrigger />

        <StaticCard
          backgroundColor="semantics.colorBackgroundSecondary"
          p={3}
          mx={1}
          border={`1px solid ${theme.colorOutlineSecondary}`}
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <Textarea
            label="Årsak..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Text variant="sm">
            <Text variant="sm" fontWeight="bold" as="span">
              OBS!
            </Text>{" "}
            Hendelsen vil bli {actionVerb.toLocaleLowerCase()}et. Den blir
            fortsatt liggende i kolonnen for hendelser uten tiltak.
          </Text>
        </StaticCard>

        <Flex direction="row" gap={1} p={4} justifyContent="space-between">
          <Button
            role="button"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              setReason("");
              setMuteDialogOpen(false);
            }}
          >
            Avbryt
          </Button>

          <Button
            role="button"
            variant="primary"
            onClick={(e) => {
              e.preventDefault();
              addMuteHistoryEntry({
                action: isMuted ? "unmuted" : "muted",
                reason,
                timestamp: new Date().toISOString(),
              });
              setReason("");
              setMuteDialogOpen(false);
              forceGlobalRecount();
            }}
          >
            {actionVerb} hendelse
          </Button>
        </Flex>
      </DropsDialogContent>
    </DialogRoot>
  );
};
